import { API } from '@xbto/api-client';
import { AccountButton, useModal, Workflow } from '@xbto/universal-components';
import { AllowedWorkflowType, Enriched } from 'common';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '~/routes';
import { DataStore } from '~/store';
import { useBeginWorkflow } from './use-begin-workflow';
import { useCtrlClickNavigation } from '~/hooks/use-ctrl-click-navigation';

export const useAccountSelector = () => {
  /**
   * Store
   */
  const accountsByProductName = DataStore.useStoreState(
    s => s.portfolio.accountsByProductName
  );
  const accounts = DataStore.useStoreState(s => s.portfolio.accounts);

  /**
   * Hooks
   */
  // TODO: The modal needs a header X close button as an option
  const beginWorkflow = useBeginWorkflow();
  const ctrlClickNavigation = useCtrlClickNavigation();
  const { open: openModal, Modal, close: closeModal } = useModal();
  const navigate = useNavigate();

  /**
   * Methods
   */
  const _getBeginWorflowDialogType = (
    workflowName?: Workflow
  ): AllowedWorkflowType | null => {
    if (workflowName === Workflow.AddCash) {
      return 'workflow-add-cash';
    }
    if (workflowName === Workflow.Buy) {
      return 'workflow-buy';
    }
    if (workflowName === Workflow.Receive) {
      return 'workflow-receive';
    }
    if (workflowName === Workflow.Sell) {
      return 'workflow-sell';
    }
    if (workflowName === Workflow.Send) {
      return 'workflow-send';
    }
    if (workflowName === Workflow.Transfer) {
      return 'workflow-transfer';
    }
    if (workflowName === Workflow.WithdrawCash) {
      return 'workflow-withdraw-cash';
    }
    return null;
  };

  const _getModalBody = (
    accounts: Enriched.ListAccountItem[],
    onSelect: (accId: string, event) => void,
    workflow?: Workflow
  ) => {
    const renderedAccounts = accounts.filter(ac => {
      if (!workflow) {
        return true;
      }
      if (workflow === Workflow.Funds) {
        // Unsure if we ever hit this, but do not show fund accounts
        return false;
      }
      if (workflow === Workflow.AddCash && ac.canReceiveCash) {
        return true;
      }
      if (
        (workflow === Workflow.Buy || workflow === Workflow.Sell) &&
        ac.canTrade
      ) {
        return true;
      }
      if (workflow === Workflow.Receive && ac.canReceiveCrypto) {
        return true;
      }
      if (workflow === Workflow.Send && ac.canSendCrypto) {
        return true;
      }
      if (
        workflow === Workflow.Transfer &&
        (ac.canAccountTransfer ||
          ac.canReceiveAccountTransfer ||
          ac.canSendAccountTransfer)
      ) {
        return true;
      }
      if (workflow === Workflow.WithdrawCash && ac.canSendCash) {
        return true;
      }
      return false;
    });
    return (
      <>
        {renderedAccounts.map(({ account }) => {
          if (!account) {
            return null;
          }
          return (
            <div className="mb-2" key={account.accountId}>
              <AccountButton
                key={account.accountId}
                account={{
                  accountNumber: account.accountNumber,
                  accountType: account.accountType,
                  label: account.label,
                }}
                onPress={e => onSelect(account.accountId, e)}
              />
            </div>
          );
        })}
      </>
    );
  };

  const openByAccountType = useCallback(
    (type: API.AccountType) => {
      // An account card was pressed, so only show accounts in that product group
      const acGroup = accountsByProductName?.find(p => p.type === type);
      if (!acGroup) {
        return;
      }
      if (acGroup.count === 0) {
        navigate(`${APP_ROUTES.AUTH_MARKETS}?assetType=Funds`);
      }
      if (acGroup.count === 1) {
        navigate(
          `${APP_ROUTES.AUTH_PORTFOLIO}/${acGroup.accounts[0].account?.accountId}`
        );
        return;
      }
      openModal({
        title: 'Select account',
        Body: _getModalBody(acGroup.accounts, (accId, event) => {
          const path = APP_ROUTES.AUTH_PORTFOLIO_ACCOUNT.replace(':id', accId);
          if (!ctrlClickNavigation(event, path)) {
            navigate(path);
          }
          closeModal();
        }),
      });
    },
    [accountsByProductName, navigate, openModal, closeModal]
  );

  const openByWorklow = useCallback(
    (workflow: Workflow) => {
      // Note: A workflow button was pressed, so show all the accounts
      if (workflow === Workflow.Funds) {
        navigate(`${APP_ROUTES.AUTH_MARKETS}?assetType=Funds`);
      }
      openModal({
        title: 'Select account',
        Body: _getModalBody(
          accounts,
          accId => {
            closeModal();

            const dialogType = _getBeginWorflowDialogType(workflow);
            if (!dialogType) {
              return;
            }

            beginWorkflow(dialogType, null, accId);
          },
          workflow
        ),
      });
    },
    [navigate, openModal, closeModal, accounts, beginWorkflow]
  );

  /**
   * Result
   */
  return {
    openByAccountType,
    openByWorklow,
    Selector: Modal,
  };
};
